import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Solutions", href: "#solutions" },
  { label: "Why SDAV", href: "#metrics" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!isHome) {
      setActiveSection("");
      return;
    }
    const sectionIds = navLinks.map((l) => l.href.replace("#", ""));
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [isHome]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/${href}`);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-18 flex items-center justify-between gap-6">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 flex-shrink-0 cursor-pointer">
          <span className="font-headline font-600 text-xl text-navy tracking-tight whitespace-nowrap">
            CUBE <span className="text-orange">MC</span>
          </span>
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href)}
              className={`font-body text-sm font-500 transition-colors duration-150 cursor-pointer whitespace-nowrap relative ${
                activeSection === link.href.replace("#", "")
                  ? "text-orange after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-[2px] after:bg-orange after:rounded-full"
                  : "text-neutral-600 hover:text-orange"
              }`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => { setMobileOpen(false); navigate("/platform"); }}
            className={`font-body text-sm font-500 transition-colors duration-150 cursor-pointer whitespace-nowrap ${
              location.pathname === "/platform" ? "text-orange" : "text-neutral-600 hover:text-orange"
            }`}
          >
            Platform
          </button>
          <button
            onClick={() => { setMobileOpen(false); navigate("/case-studies"); }}
            className={`font-body text-sm font-500 transition-colors duration-150 cursor-pointer whitespace-nowrap ${
              location.pathname.startsWith("/case-studies") ? "text-orange" : "text-neutral-600 hover:text-orange"
            }`}
          >
            Case Studies
          </button>
          <button
            onClick={() => { setMobileOpen(false); navigate("/about"); }}
            className={`font-body text-sm font-500 transition-colors duration-150 cursor-pointer whitespace-nowrap ${
              location.pathname === "/about" ? "text-orange" : "text-neutral-600 hover:text-orange"
            }`}
          >
            About
          </button>
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <a
            href="https://cubegroup.global"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="font-body whitespace-nowrap cursor-pointer transition-colors"
            style={{ fontSize: "12px", color: "#888880" }}
          >
            cubegroup.global ↗
          </a>
          <button
            onClick={() => navigate("/request-a-call")}
            className="btn-primary"
          >
            Request a Call
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-12 h-12 flex items-center justify-center text-neutral-600 hover:text-navy transition-colors cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden bg-white border-t border-neutral-200 flex flex-col gap-0 shadow-lg overflow-hidden transition-all duration-300 ease-out ${
          mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {navLinks.map((link) => (
          <button
            key={link.label}
            onClick={() => handleNavClick(link.href)}
            className={`font-body text-sm font-500 py-4 px-5 text-left border-b border-neutral-100 transition-colors cursor-pointer min-h-[48px] ${
              activeSection === link.href.replace("#", "")
                ? "text-orange font-600"
                : "text-neutral-700 hover:text-orange"
            }`}
          >
            {link.label}
          </button>
        ))}
        <button
          onClick={() => { setMobileOpen(false); navigate("/platform"); }}
          className={`font-body text-sm font-500 py-4 px-5 text-left border-b border-neutral-100 transition-colors cursor-pointer min-h-[48px] ${
            location.pathname === "/platform" ? "text-orange font-600" : "text-neutral-700 hover:text-orange"
          }`}
        >
          Platform
        </button>
        <button
          onClick={() => { setMobileOpen(false); navigate("/case-studies"); }}
          className={`font-body text-sm font-500 py-4 px-5 text-left border-b border-neutral-100 transition-colors cursor-pointer min-h-[48px] ${
            location.pathname.startsWith("/case-studies") ? "text-orange font-600" : "text-neutral-700 hover:text-orange"
          }`}
        >
          Case Studies
        </button>
        <button
          onClick={() => { setMobileOpen(false); navigate("/about"); }}
          className="font-body text-sm font-500 text-neutral-700 hover:text-orange py-4 px-5 text-left border-b border-neutral-100 transition-colors cursor-pointer min-h-[48px]"
        >
          About
        </button>
        <div className="px-5 py-4">
          <button
            onClick={() => { setMobileOpen(false); navigate("/request-a-call"); }}
            className="btn-primary w-full text-center"
          >
            Request a Call
          </button>
        </div>
      </div>
    </nav>
  );
}
